import React from 'react';
import { Chip, LinearProgress, Stack, Typography } from '@mui/material';
import {
  GridCellParams,
  GridColDef,
  GridValidRowModel,
} from '@mui/x-data-grid';

import { FDataGrid } from '../grid';
import {
  BudgetControlAllocation,
  BudgetControlAllocationSaveItem,
} from './budgetcontrol.models';

type Props = {
  rows: BudgetControlAllocation[];
  capacityAmount: number;
  currencyCode: string;
  editable: boolean;
  saving?: boolean;
  onRowsChange: (rows: BudgetControlAllocation[]) => void;
  onSave: (rows: BudgetControlAllocationSaveItem[]) => Promise<void>;
};

const money = (value: number, currency: string): string =>
  new Intl.NumberFormat('en-GH', {
    style: 'currency',
    currency: currency || 'XOF',
    maximumFractionDigits: 0,
  }).format(value || 0);

export default class BudgetControlAllocationGrid extends React.PureComponent<Props> {
  private validateRow = (row: BudgetControlAllocation): string => {
    const allocationPercent = Number(row.allocationpercent);
    const warningPercent = Number(row.warningpercent);

    if (!Number.isFinite(allocationPercent)) {
      return 'Ceiling percentage must be numeric.';
    }

    if (allocationPercent < 0 || allocationPercent > 100) {
      return 'Ceiling percentage must be between 0 and 100.';
    }

    if (!Number.isFinite(warningPercent)) {
      return 'Warning percentage must be numeric.';
    }

    if (warningPercent < 0 || warningPercent > 100) {
      return 'Warning percentage must be between 0 and 100.';
    }

    return '';
  };

  private isCellEditable = (
    params: GridCellParams<BudgetControlAllocation>,
  ): boolean => {
    if (!this.props.editable) return false;

    return ['allocationpercent', 'warningpercent', 'reason', 'remarks'].includes(
      params.field,
    );
  };

  private computedStatus = (
    ceilingAmount: number,
    allocatedAmount: number,
    warningPercent: number,
  ): BudgetControlAllocation['controlstatus'] => {
    if (ceilingAmount <= 0) {
      return allocatedAmount > 0 ? 'EXCEEDED' : 'AVAILABLE';
    }

    const utilisation = (allocatedAmount * 100) / ceilingAmount;

    if (utilisation > 100) return 'EXCEEDED';
    if (utilisation >= warningPercent) return 'WARNING';
    return 'AVAILABLE';
  };

  private processRowUpdate = (
    newRow: GridValidRowModel,
    oldRow: GridValidRowModel,
  ): BudgetControlAllocation => {
    const original = oldRow as BudgetControlAllocation;
    const allocationPercent = Number(newRow.allocationpercent || 0);
    const warningPercent = Number(newRow.warningpercent || 90);
    const capacityAmount = Number(this.props.capacityAmount || 0);
    const allocatedAmount = Number(original.allocatedamount || 0);
    const ceilingAmount = (capacityAmount * allocationPercent) / 100;
    const availableAmount = ceilingAmount - allocatedAmount;
    const utilisationPercent =
      ceilingAmount > 0 ? (allocatedAmount * 100) / ceilingAmount : 0;

    const updated: BudgetControlAllocation = {
      ...original,
      ...(newRow as BudgetControlAllocation),

      // Grid/database identity and governed dimensions cannot be edited.
      id: original.id,
      allocationid: original.allocationid,
      allocationix: original.allocationix,
      controlareaid: original.controlareaid,
      controlareacode: original.controlareacode,
      controlareaname: original.controlareaname,

      allocationpercent: allocationPercent,
      warningpercent: warningPercent,
      ceilingamount: ceilingAmount,
      allocatedamount: allocatedAmount,
      availableamount: availableAmount,
      utilisationpercent: utilisationPercent,
      controlstatus: this.computedStatus(
        ceilingAmount,
        allocatedAmount,
        warningPercent,
      ),
      _state: 'dirty',
      __dirty: true,
    };

    updated.__error = this.validateRow(updated);

    this.props.onRowsChange(
      this.props.rows.map((row) =>
        String(row.id) === String(original.id) ? updated : row,
      ),
    );

    return updated;
  };

  private columns = (): GridColDef<BudgetControlAllocation>[] => [
    {
      field: 'controlareaname',
      headerName: 'Budget Area',
      minWidth: 190,
      flex: 1,
      editable: false,
      cellClassName: 'budgetcontrol-cell-governed',
    },
    {
      field: 'allocationpercent',
      headerName: 'Ceiling %',
      type: 'number',
      width: 125,
      editable: true,
      valueParser: (value) => Number(value || 0),
      cellClassName: 'budgetcontrol-cell-input',
    },
    {
      field: 'ceilingamount',
      headerName: 'Ceiling Amount',
      width: 165,
      editable: false,
      valueFormatter: (value: any) =>
        money(Number(value || 0), this.props.currencyCode),
      cellClassName: 'budgetcontrol-cell-computed',
    },
    {
      field: 'allocatedamount',
      headerName: 'Consumed',
      width: 155,
      editable: false,
      valueFormatter: (value: any) =>
        money(Number(value || 0), this.props.currencyCode),
      cellClassName: 'budgetcontrol-cell-computed',
    },
    {
      field: 'availableamount',
      headerName: 'Available',
      width: 155,
      editable: false,
      valueFormatter: (value: any) =>
        money(Number(value || 0), this.props.currencyCode),
      cellClassName: 'budgetcontrol-cell-computed',
    },
    {
      field: 'utilisationpercent',
      headerName: 'Utilisation',
      minWidth: 170,
      flex: 0.8,
      editable: false,
      cellClassName: 'budgetcontrol-cell-computed',
      renderCell: (params) => {
        const value = Number(params.value || 0);
        return (
          <Stack sx={{ width: '100%' }} spacing={0.4}>
            <Typography variant="caption">{value.toFixed(2)}%</Typography>
            <LinearProgress
              variant="determinate"
              value={Math.min(Math.max(value, 0), 100)}
            />
          </Stack>
        );
      },
    },
    {
      field: 'warningpercent',
      headerName: 'Warning %',
      type: 'number',
      width: 120,
      editable: true,
      valueParser: (value) => Number(value || 0),
      cellClassName: 'budgetcontrol-cell-input',
    },
    {
      field: 'controlstatus',
      headerName: 'Status',
      width: 125,
      editable: false,
      cellClassName: 'budgetcontrol-cell-computed',
      renderCell: (params) => {
        const status = String(params.value || 'AVAILABLE').toUpperCase();
        const color =
          status === 'EXCEEDED'
            ? 'error'
            : status === 'WARNING'
              ? 'warning'
              : 'success';

        return <Chip size="small" color={color as any} label={status} />;
      },
    },
    {
      field: '__error',
      headerName: 'Validation',
      minWidth: 190,
      flex: 0.8,
      editable: false,
      cellClassName: 'budgetcontrol-cell-validation',
    },
  ];

  private submit = async () => {
    const invalidRows = this.props.rows.filter(
      (row) => Boolean(row.__error || this.validateRow(row)),
    );

    if (invalidRows.length > 0) {
      throw new Error('Correct invalid Budget Control allocation values first.');
    }

    const total = this.props.rows.reduce(
      (sum, row) => sum + Number(row.allocationpercent || 0),
      0,
    );

    if (total > 100) {
      throw new Error(
        `Total allocation cannot exceed 100%. Current total: ${total.toFixed(2)}%.`,
      );
    }

    await this.props.onSave(
      this.props.rows.map((row) => ({
        controlAreaCode: row.controlareacode,
        allocationPercent: Number(row.allocationpercent || 0),
        warningPercent: Number(row.warningpercent || 90),
        reason: row.reason,
        remarks: row.remarks,
        stamp: row.stamp,
      })),
    );
  };

  render() {
    const dirtyCount = this.props.rows.filter((row) => row.__dirty).length;
    const invalidCount = this.props.rows.filter((row) =>
      Boolean(row.__error || this.validateRow(row)),
    ).length;

    return (
      <FDataGrid
        rows={this.props.rows}
        columns={this.columns()}
        title="Controlled Budget Distribution"
        subtitle="Amber cells are planner inputs. Blue cells are computed from approved capacity and downstream consumption."
        loading={this.props.saving}
        height={440}
        getRowId={(row: BudgetControlAllocation) => row.id}
        isCellEditable={this.isCellEditable}
        processRowUpdate={this.processRowUpdate}
        onProcessRowUpdateError={(error: unknown) => console.error(error)}
        getRowClassName={(params) =>
          params.row.__error
            ? 'budgetcontrol-row-error'
            : params.row.__dirty
              ? 'budgetcontrol-row-dirty'
              : 'budgetcontrol-row-clean'
        }
        saveAll={
          !this.props.editable ||
          dirtyCount === 0 ||
          invalidCount > 0 ||
          this.props.saving
            ? undefined
            : this.submit
        }
        pageSizeOptions={[10, 25, 50]}
        sx={{
          '& .budgetcontrol-cell-input': {
            bgcolor: 'warning.lighter',
            fontWeight: 600,
          },
          '& .budgetcontrol-cell-input:hover': {
            bgcolor: 'warning.light',
          },
          '& .budgetcontrol-cell-computed': {
            bgcolor: 'info.lighter',
            color: 'text.secondary',
          },
          '& .budgetcontrol-cell-governed': {
            bgcolor: 'action.hover',
            fontWeight: 600,
          },
          '& .budgetcontrol-cell-validation': {
            color: 'error.main',
          },
          '& .budgetcontrol-row-dirty': {
            boxShadow: 'inset 3px 0 0 0',
            boxShadowColor: 'warning.main',
          },
          '& .budgetcontrol-row-error': {
            bgcolor: 'error.lighter',
          },
        }}
      />
    );
  }
}
